import React, { createContext, useCallback, useState } from 'react';

// The environment every API-key-scoped and webhook-scoped view in the dev
// portal operates in. Sandbox ("test") and Live keys are isolated
// server-side — this context is the single source of truth for which one
// the merchant is currently viewing across /developers/* (topbar switcher,
// API keys page, and any future page that needs it), replacing what used to
// be independent local toggles per page.
export type Environment = 'test' | 'live';

type IEnvironmentContext = {
    env: Environment;
    setEnv: (env: Environment) => void;
};

const STORAGE_KEY = 'dev-portal-environment';

// Safe default is Sandbox — a merchant should never land on Live by
// accident, so a missing/corrupted/tampered stored value all fall back here.
function readStoredEnv(): Environment {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'live' ? 'live' : 'test';
    } catch {
        // localStorage inaccessible (private browsing / sandboxed iframe) —
        // fall back to the safe default; the toggle still works in-memory
        // for the rest of the session, it just won't persist across reloads.
        return 'test';
    }
}

export const EnvironmentContext = createContext<IEnvironmentContext>({
    env: 'test',
    setEnv: () => {}
});

export const EnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [env, setEnvState] = useState<Environment>(readStoredEnv);

    const setEnv = useCallback((next: Environment) => {
        setEnvState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // Same fallback as readStoredEnv — the in-memory toggle still
            // works this session even if persistence fails.
        }
    }, []);

    return (
        <EnvironmentContext.Provider value={{ env, setEnv }}>{children}</EnvironmentContext.Provider>
    );
};
